import { Code, ConnectError } from "@connectrpc/connect";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";
import toast from "react-hot-toast";
import type { MypaymentChargeOrder } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import ArrowOutward from "~icons/material-symbols/arrow-outward";
import CurrencyExchange from "~icons/material-symbols/currency-exchange";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import { useFooter } from "../../../contexts/footer-context";
import {
  canUseOsakiChargeBonusCharge,
  getCurrentOsakiChargeBonusCampaignStatus,
} from "../../../features/charge/osaki-charge-bonus-campaign";
import {
  pageCss,
  primaryButtonCss,
  secondaryButtonCss,
} from "../../../features/charge/styles";
import type { WalletClient } from "../../../libs/client";
import { formatDate, getCurrentTime } from "../../../libs/date";
import { t } from "../../../libs/i18n";
import { formatNumber } from "../../../libs/utils";

const BALANCE_ROUTE_IDS = new Set(["/_authed/", "/_authed/balances/"]);

export const Route = createFileRoute("/_authed/charge/mypayment")({
  beforeLoad: ({ context: { tenant } }) => {
    if (!tenant.feature.charge) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context: { client, tenant } }) => {
    if (tenant.slug === "miyagi") {
      const [campaignStatuses, currentTime] = await Promise.all([
        client.getMyCampaignStatuses({}),
        getCurrentTime(),
      ]);
      const osakiChargeBonusStatus = getCurrentOsakiChargeBonusCampaignStatus(
        campaignStatuses,
        currentTime.date,
      );

      if (
        !canUseOsakiChargeBonusCharge(osakiChargeBonusStatus, currentTime.date)
      ) {
        throw redirect({ to: "/" });
      }
    }

    const order = await getAvailableAwaitingMypaymentOrder(client);
    if (!order) {
      throw redirect({ to: "/charge" });
    }
    return { order };
  },
  component: MypaymentCharge,
});

function MypaymentCharge() {
  const { order } = Route.useLoaderData();
  const { setDisabled: setFooterDisabled } = useFooter();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const amount = orderAmount(order);
  const barcodeAvailableUntil = order.barcodeAvailableUntil?.toDate() ?? null;

  useEffect(() => {
    // コンビニ支払いは外部バーコード画面で非同期完了するため、
    // 完了後に参照される履歴・残高のキャッシュを事前に破棄する。
    const resetPaymentResultCaches = () => {
      queryClient.resetQueries({ queryKey: ["transactions"] });
      router.clearCache({
        filter: (match) => BALANCE_ROUTE_IDS.has(match.routeId),
      });
    };

    resetPaymentResultCaches();

    return () => {
      resetPaymentResultCaches();
    };
  }, [queryClient, router]);

  useEffect(() => {
    setFooterDisabled(true);
    return () => setFooterDisabled(false);
  }, [setFooterDisabled]);

  const showBarcode = () => {
    if (!order.barcodeUrl) {
      toast.error(t("バーコードを表示できませんでした"));
      return;
    }
    window.location.href = order.barcodeUrl;
  };

  return (
    <>
      <Header
        title={t("コンビニでチャージ")}
        onClickBack={() => navigate({ to: "/charge" })}
      />
      <main className={pageCss}>
        <p
          className={css({
            fontSize: "14px",
            lineHeight: "1.4",
            color: "text.primary",
          })}
        >
          {t(
            "「バーコードを表示」をタップし、表示されたバーコードをレジで提示してください。",
          )}
        </p>

        <section
          className={css({
            bg: "surface.primary",
            rounded: "16px",
            px: "24px",
            py: "24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
          })}
        >
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            })}
          >
            <span
              className={css({
                bg: "surface.accentSuccessLight",
                color: "text.accentSuccess",
                rounded: "full",
                px: "12px",
                py: "2px",
                display: "flex",
                alignItems: "baseline",
                gap: "4px",
              })}
            >
              <span className={css({ fontSize: "16px", lineHeight: "1.5" })}>
                {formatNumber(amount)}
              </span>
              <span
                className={css({
                  fontSize: "12px",
                  lineHeight: "1.35",
                  fontWeight: "600",
                })}
              >
                {t("円分")}
              </span>
            </span>
            <CurrencyExchange
              className={css({ color: "text.accentSuccess" })}
              fontSize={40}
            />
          </div>

          <button
            type="button"
            className={primaryButtonCss}
            onClick={showBarcode}
          >
            {t("バーコードを表示")}
            <ArrowOutward />
          </button>

          <div
            className={css({
              bg: "surface.secondary",
              rounded: "8px",
              px: "16px",
              py: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              w: "full",
              fontSize: "12px",
              lineHeight: "1.3",
            })}
          >
            <InfoRow
              label={t("有効期限")}
              value={
                barcodeAvailableUntil
                  ? formatDate(barcodeAvailableUntil, "date")
                  : ""
              }
            />
            <InfoRow label={t("お問い合わせ番号")} value={order.gmoOrderId} />
          </div>
        </section>

        <button
          type="button"
          className={secondaryButtonCss}
          onClick={() => navigate({ to: "/" })}
        >
          {t("ホームに戻る")}
        </button>
      </main>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      className={css({
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "16px",
        w: "full",
      })}
    >
      <span className={css({ color: "text.tertiary", whiteSpace: "nowrap" })}>
        {label}
      </span>
      <span
        className={css({
          color: "text.secondary",
          minW: 0,
          textAlign: "right",
          overflowWrap: "anywhere",
        })}
      >
        {value}
      </span>
    </div>
  );
}

async function getAvailableAwaitingMypaymentOrder(client: WalletClient) {
  try {
    const response =
      await client.getAvailableAwaitingPaymentMypaymentChargeOrder({});
    return response.order ?? null;
  } catch (error) {
    if (error instanceof ConnectError && error.code === Code.NotFound) {
      return null;
    }
    throw error;
  }
}

function orderAmount(order: MypaymentChargeOrder) {
  return Number(order.price) * Number(order.quantity);
}
